import { Controller, useWatch, type Control } from 'react-hook-form';
import { t } from '@app/i18n';
import { withEmphasis } from '@app/i18n/emphasis';
import { Field, FieldGrid, InfoTip, SearchCombobox, TextInput } from '@zarya/ui';
import {
  ROLES,
  bypassApplies,
  roleLabel,
  roleLabelIsValue,
  roleSummary,
  PASSWORD_MIN,
  type ModePassword,
  type Role,
  type UserFormValues,
} from './UserModel';
import s from './Users.module.css';

export interface UserFormProps {
  control: Control<UserFormValues>;
  /** `true` = edição: e-mail somente leitura e sem senha inicial. */
  editando: boolean;
  /** Modo de senha escolhido (o drawer observa o campo). */
  modoSenha: ModePassword;
  onSubmit: () => void;
  /** `id` do `<form>` — o botão do rodapé do drawer submete por `form=`. */
  formId: string;
}

/**
 * Campos do usuário (T22, FR-106).
 *
 * Na edição o e-mail é somente leitura: ele identifica a pessoa no vínculo com o
 * Google e é imutável por contrato (12.2). A senha inicial só existe na criação —
 * depois dela, a única forma de trocar é **Redefinir senha** (FR-109).
 */
export function UserForm({ control, editando, modoSenha, onSubmit, formId }: UserFormProps) {
  // O Bypass Approval some com o papel da esteira (aim-57a.4): ele é sobre o candidato
  // de Access Control, que a esteira não pede. `forCreate`/`forUpdate` o mandam desligado.
  const role = useWatch({ control, name: 'role' });

  return (
    <form id={formId} onSubmit={onSubmit} noValidate>
      <FieldGrid>
        <Controller
          control={control}
          name="name"
          render={({ field, fieldState }) => (
            <Field
              label={t('users.form.field.name')}
              span={12}
              required
              error={fieldState.error?.message}
            >
              {(props) => <TextInput {...props} {...field} autoComplete="off" />}
            </Field>
          )}
        />

        <Controller
          control={control}
          name="email"
          render={({ field, fieldState }) => (
            <Field
              label={t('users.form.field.email')}
              span={12}
              required
              error={fieldState.error?.message}
              help={t(
                editando ? 'users.form.field.email.help.edit' : 'users.form.field.email.help.new',
              )}
              tip={t('users.form.field.email.tip')}
            >
              {(props) => (
                <TextInput
                  {...props}
                  {...field}
                  mono
                  type="email"
                  autoComplete="off"
                  readOnly={editando}
                  placeholder={t('users.form.field.email.placeholder')}
                />
              )}
            </Field>
          )}
        />

        <Controller
          control={control}
          name="role"
          render={({ field, fieldState }) => (
            <Field
              label={t('users.form.field.role')}
              span={12}
              required
              error={fieldState.error?.message}
              tip={t('users.form.field.role.tip')}
              help={roleSummary(field.value as Role)}
            >
              {/*
                O `className` do `Field` é para `<input>`; o combobox tem o seu.
                E o `label` vai explícito: `<label htmlFor>` não nomeia um
                `<button>`, que é o que o gatilho do combobox é.
              */}
              {({ className: _fin, ...props }) => (
                <SearchCombobox
                  {...props}
                  label={t('users.form.field.role')}
                  // O `label` da opção é o **valor do contrato** (`admin`,
                  // `approver`, `viewer`), como o `read`/`write` do risco: é o
                  // que o servidor grava, e por isso não se traduz. A conta da
                  // esteira é a exceção, com nome de gente: ver `roleLabel`.
                  options={ROLES.map((papel) => ({
                    value: papel,
                    label: roleLabel(papel),
                    hint: roleSummary(papel),
                    mono: roleLabelIsValue(papel),
                  }))}
                  value={field.value}
                  onChange={(valor) => field.onChange(valor ?? 'viewer')}
                  placeholder={t('users.form.field.role.placeholder')}
                />
              )}
            </Field>
          )}
        />

        {/*
          Bypass Approval (D8 de 02/10): o rótulo é o nome do recurso, que em pt-BR é
          «Aprovação automática» desde 10/10 (decisão do mrolim, aim-rtr) e em en segue
          «Bypass Approval»; a explicação fica no ⓘ, como no protótipo aprovado.
        */}
        {bypassApplies(role as Role) ? (
          <Controller
            control={control}
            name="autoApproveRequests"
            render={({ field }) => (
              <div className={s.bypass}>
                <label className={s.opcao}>
                  <input
                    type="checkbox"
                    name={field.name}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    onBlur={field.onBlur}
                    data-testid="user-auto-approve-requests"
                  />
                  {t('users.form.field.autoApproveRequests')}
                </label>
                <InfoTip
                  text={t('users.form.field.autoApproveRequests.tip')}
                  label={t('common.ui.about', { label: t('users.form.field.autoApproveRequests') })}
                />
              </div>
            )}
          />
        ) : null}

        {!editando ? (
          <>
            <Controller
              control={control}
              name="modoSenha"
              render={({ field }) => (
                <fieldset className={s.modoSenha}>
                  <legend className={s.modoSenhaLegenda}>{t('users.form.password.legend')}</legend>
                  <div className={s.opcoes}>
                    <label className={s.opcao}>
                      <input
                        type="radio"
                        name={field.name}
                        value="gerar"
                        checked={field.value === 'gerar'}
                        onChange={() => field.onChange('gerar')}
                      />
                      {t('users.form.password.generate')}
                    </label>
                    <label className={s.opcao}>
                      <input
                        type="radio"
                        name={field.name}
                        value="digitar"
                        checked={field.value === 'digitar'}
                        onChange={() => field.onChange('digitar')}
                      />
                      {t('users.form.password.type')}
                    </label>
                  </div>
                </fieldset>
              )}
            />

            {modoSenha === 'digitar' ? (
              <Controller
                control={control}
                name="password"
                render={({ field, fieldState }) => (
                  <Field
                    label={t('users.form.field.password')}
                    span={12}
                    required
                    error={fieldState.error?.message}
                    help={t('users.form.field.password.help', { min: PASSWORD_MIN })}
                  >
                    {(props) => (
                      <TextInput
                        {...props}
                        {...field}
                        type="password"
                        autoComplete="new-password"
                      />
                    )}
                  </Field>
                )}
              />
            ) : (
              // Uma chave só, com `*trecho*`: o `withEmphasis` (promovido pelo
              // p5 a partir do Simulador) põe o `<strong>` onde a **tradução**
              // marcar. Estava em duas chaves porque o mecanismo não existia
              // quando extraí esta tela.
              <p className={s.aviso}>{withEmphasis(t('users.form.password.notice'))}</p>
            )}
          </>
        ) : null}
      </FieldGrid>
    </form>
  );
}
